import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Play, Heart, Share2, Users, DollarSign, Trophy, Music, Loader2, BarChart3, Swords } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import SEO from "../components/SEO";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Inline mini area chart using SVG (avoid heavy recharts import)
function MiniChart({ data, dataKey = "revenue", color = "#00f3ff", height = 120 }) {
  if (!data || data.length === 0) return <div className="h-[120px] flex items-center justify-center text-zinc-600 text-xs">No data yet</div>;
  const values = data.map(d => d[dataKey]);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 100;
  const h = height;
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((d[dataKey] - min) / range) * (h * 0.8) - h * 0.1;
    return `${x},${y}`;
  }).join(" ");
  const areaPoints = `0,${h} ${points} ${w},${h}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#grad-${color.replace('#','')})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

export default function ProducerAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/api/producers/analytics`, { headers })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen bg-dark flex items-center justify-center pt-20"><Loader2 className="w-8 h-8 animate-spin text-neon-cyan" /></div>;
  if (!token) return <div className="min-h-screen bg-dark flex items-center justify-center pt-20"><p className="text-zinc-400">Sign in to view analytics</p></div>;
  if (!data) return <div className="min-h-screen bg-dark flex items-center justify-center pt-20"><p className="text-zinc-400">No analytics data available</p></div>;

  const ov = data.overview;

  return (
    <div className="min-h-screen bg-dark pt-24 pb-16 px-4" data-testid="producer-analytics-page">
      <SEO title="Producer Analytics" description="Your beat performance, revenue, and audience metrics." />
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <Badge className="bg-neon-cyan/20 text-neon-cyan border-0 mb-3">Analytics</Badge>
          <h1 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight">Producer Dashboard</h1>
          <p className="text-zinc-500 text-sm mt-1">Track your beats, revenue, and audience.</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
          <KPICard icon={DollarSign} label="Revenue" value={`$${ov.total_revenue}`} color="neon-cyan" />
          <KPICard icon={DollarSign} label="Royalties" value={`$${ov.total_royalties}`} sub={ov.pending_royalties > 0 ? `$${ov.pending_royalties} pending` : undefined} color="neon-green" />
          <KPICard icon={Play} label="Total Plays" value={ov.total_plays.toLocaleString()} color="neon-magenta" />
          <KPICard icon={Heart} label="Total Likes" value={ov.total_likes.toLocaleString()} color="red-400" />
          <KPICard icon={Users} label="Followers" value={ov.followers.toLocaleString()} color="neon-yellow" />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <KPICard icon={Music} label="Beats" value={ov.total_beats} color="zinc-300" small />
          <KPICard icon={Share2} label="Shares" value={ov.total_shares} color="zinc-300" small />
          <KPICard icon={Swords} label="Battles" value={`${ov.battle_wins}/${ov.battle_total}`} color="zinc-300" small />
          <KPICard icon={Trophy} label="Win Rate" value={ov.battle_total > 0 ? `${Math.round((ov.battle_wins / ov.battle_total) * 100)}%` : "N/A"} color="yellow-400" small />
        </div>

        {/* Revenue Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="glass border border-white/10 rounded-xl p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-sm uppercase tracking-wider flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-neon-cyan" /> Revenue (Last 30 Days)
            </h2>
          </div>
          <MiniChart data={data.revenue_chart} dataKey="revenue" color="#00f3ff" height={160} />
          {data.revenue_chart.length > 0 && (
            <div className="flex justify-between mt-2 text-[10px] text-zinc-600">
              <span>{data.revenue_chart[0]?.date}</span>
              <span>{data.revenue_chart[data.revenue_chart.length - 1]?.date}</span>
            </div>
          )}
        </motion.div>

        {/* Top Beats */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="glass border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-heading text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-neon-magenta" /> Top Performing Beats
          </h2>
          {data.beat_stats.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">No beats yet. Upload your first beat!</p>
          ) : (
            <div className="space-y-3">
              {data.beat_stats.map((beat, i) => (
                <div key={beat.beat_id} className="flex items-center gap-4 p-3 rounded-lg bg-zinc-800/40 hover:bg-zinc-800/60 transition" data-testid={`beat-stat-${beat.beat_id}`}>
                  <span className="text-zinc-600 font-mono text-sm w-6 text-right">{i + 1}</span>
                  <div className="w-10 h-10 rounded bg-zinc-700 overflow-hidden flex-shrink-0">
                    {beat.image_url ? <img src={beat.image_url} alt="" className="w-full h-full object-cover" loading="lazy" /> : <Music className="w-5 h-5 text-zinc-600 m-auto mt-2.5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{beat.title}</p>
                    <p className="text-zinc-500 text-xs">{beat.genre}</p>
                  </div>
                  <div className="flex gap-4 text-xs text-zinc-400">
                    <span className="flex items-center gap-1"><Play className="w-3 h-3" />{beat.plays}</span>
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{beat.likes}</span>
                    <span className="flex items-center gap-1"><Share2 className="w-3 h-3" />{beat.shares}</span>
                  </div>
                  <span className="text-neon-cyan font-bold text-sm">${beat.revenue}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, color = "neon-cyan", small = false }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
      className={`glass border border-white/10 rounded-xl ${small ? "p-3" : "p-4"}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`${small ? "w-3 h-3" : "w-4 h-4"} text-${color}`} />
        <span className={`text-zinc-500 ${small ? "text-[10px]" : "text-xs"} uppercase tracking-wider`}>{label}</span>
      </div>
      <p className={`text-white font-bold ${small ? "text-base" : "text-xl"}`}>{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
    </motion.div>
  );
}
